// components/Navbar.jsx — fully responsive + i18n (EN/HI/GU)
import { useState, useRef, useEffect } from "react";
import { Logo, Button, Icons } from "./UI";
import { useLang } from "../App";
import { useScrolled } from "../hooks";

const LINKS = ["home", "about", "products", "contact"];
const LANGS = [
  ["en", "EN", "English"],
  ["hi", "हि", "हिंदी"],
  ["gu", "ગુ", "ગુજ."],
];

const Navbar = ({ page, setPage }) => {
  const { lang, t, setLang } = useLang();
  const scrolled = useScrolled(40);
  const [open, setOpen] = useState(false);
  const [srchOpen, setSrchOpen] = useState(false);
  const [srch, setSrch] = useState("");
  const srchRef = useRef(null);

  useEffect(() => {
    if (srchOpen) srchRef.current?.focus();
  }, [srchOpen]);
  // Close drawer on page change
  useEffect(() => {
    setOpen(false);
  }, [page]);
  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const glass = !scrolled && page === "home";
  const txtCol = (active) =>
    glass
      ? active
        ? "#fff"
        : "rgba(255,255,255,.75)"
      : active
        ? "var(--pine)"
        : "var(--stone)";

  const go = (k) => {
    setPage(k);
    setOpen(false);
    setSrchOpen(false);
  };

  return (
    <>
      {/* ════════ MAIN NAV ════════════════════════════════════════════════ */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 900,
          height: "var(--nav-h)",
          background: glass ? "transparent" : "rgba(255,255,255,.93)",
          backdropFilter: glass ? "none" : "blur(20px)",
          borderBottom: glass ? "none" : "1px solid rgba(0,0,0,.06)",
          transition: "all .4s var(--ease)",
        }}
      >
        <div
          className="wrap"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {/* Logo */}
          <Logo inverted={glass} onClick={() => go("home")} />
          <span style={{ flex: 1 }} />

          {/* ── Desktop nav links (hidden on mobile) ── */}
          <div className="hide-sm" style={{ display: "flex", gap: 2 }}>
            {LINKS.map((k) => (
              <button
                key={k}
                onClick={() => go(k)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "7px 13px",
                  borderRadius: 8,
                  fontFamily: "var(--sans)",
                  fontSize: 14,
                  fontWeight: page === k ? 600 : 400,
                  color: txtCol(page === k),
                  transition: "all .2s",
                  position: "relative",
                }}
              >
                {t(k)}
                {page === k && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 2,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: glass ? "var(--amber)" : "var(--emerald)",
                    }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* ── Desktop search ── */}
          <div className="hide-sm">
            {srchOpen ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "var(--foam)",
                  borderRadius: 10,
                  padding: "7px 12px",
                  border: "1.5px solid var(--mint)",
                }}
              >
                <Icons.Search size={14} />
                <input
                  ref={srchRef}
                  value={srch}
                  onChange={(e) => setSrch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      go("products");
                      setSrchOpen(false);
                    }
                    if (e.key === "Escape") setSrchOpen(false);
                  }}
                  placeholder={t("searchPlaceholder")}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: 14,
                    width: 170,
                    fontFamily: "var(--sans)",
                    color: "var(--ink)",
                  }}
                />
                <button
                  onClick={() => setSrchOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--stone)",
                    display: "flex",
                  }}
                >
                  <Icons.Close size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSrchOpen(true)}
                style={{
                  background: glass ? "rgba(255,255,255,.12)" : "var(--foam)",
                  border: "none",
                  borderRadius: 10,
                  width: 37,
                  height: 37,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: glass ? "#fff" : "var(--pine)",
                  transition: "all .2s",
                }}
              >
                <Icons.Search size={15} />
              </button>
            )}
          </div>

          {/* ── Desktop language switcher ── */}
          <div
            className="hide-sm"
            style={{
              display: "flex",
              gap: 1,
              background: glass ? "rgba(255,255,255,.1)" : "var(--foam)",
              borderRadius: 10,
              padding: 3,
            }}
          >
            {LANGS.map(([code, label]) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                style={{
                  padding: "5px 10px",
                  border: "none",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "var(--sans)",
                  transition: "all .2s",
                  background:
                    lang === code
                      ? glass
                        ? "rgba(255,255,255,.25)"
                        : "#fff"
                      : "transparent",
                  color:
                    lang === code
                      ? glass
                        ? "#fff"
                        : "var(--pine)"
                      : glass
                        ? "rgba(255,255,255,.6)"
                        : "var(--stone)",
                  boxShadow:
                    lang === code && !glass
                      ? "0 1px 4px rgba(0,0,0,.1)"
                      : "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── Desktop admin btn ── */}
          {/* <div className="hide-sm">
            <Button variant="gold" size="sm" onClick={() => go("admin")}>
              {t("admin")} ↗
            </Button>
          </div> */}

          {/* ── Mobile hamburger (always visible on small screens) ── */}
          <button
            onClick={() => setOpen(true)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 6,
              color: glass ? "#fff" : "var(--pine)",
              display: "flex",
              marginLeft: 2,
            }}
            // Show on all screens — desktop hides it via CSS
            className="show-mobile"
          >
            <Icons.Menu size={24} color={glass ? "#fff" : "var(--pine)"} />
          </button>
        </div>
      </nav>

      {/* ════════ MOBILE DRAWER ════════════════════════════════════════════ */}

      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999,
          background: "rgba(0,0,0,.48)",
          backdropFilter: "blur(4px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity .3s",
        }}
      />

      {/* Drawer panel — slides in from right */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          width: 290,
          maxWidth: "88vw",
          background: "#fff",
          boxShadow: "-20px 0 60px rgba(0,0,0,.15)",
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform .32s var(--ease)",
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            padding: "18px 18px",
            borderBottom: "1px solid var(--foam)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Logo />
          <button
            onClick={() => setOpen(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--stone)",
              display: "flex",
              padding: 4,
            }}
          >
            <Icons.Close size={18} />
          </button>
        </div>

        {/* Drawer scrollable body */}
        <div style={{ flex: 1, padding: "12px", overflowY: "auto" }}>
          {/* Nav links — all labels from t() */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginBottom: 16,
            }}
          >
            {LINKS.map((k) => (
              <button
                key={k}
                onClick={() => go(k)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "13px 16px",
                  border: "none",
                  borderRadius: 10,
                  cursor: "pointer",
                  marginBottom: 2,
                  fontFamily: "var(--sans)",
                  fontSize: 15,
                  fontWeight: page === k ? 600 : 400,
                  background: page === k ? "var(--foam)" : "transparent",
                  color: page === k ? "var(--pine)" : "var(--ink)",
                  transition: "all .2s",
                }}
                onMouseEnter={(e) => {
                  if (page !== k)
                    e.currentTarget.style.background = "var(--snow)";
                }}
                onMouseLeave={(e) => {
                  if (page !== k)
                    e.currentTarget.style.background = "transparent";
                }}
              >
                {t(k)}
              </button>
            ))}
          </div>

          <div
            style={{
              height: 1,
              background: "var(--foam)",
              margin: "4px 0 16px",
            }}
          />

          {/* Language switcher — full labels, active highlighted */}
          <div
            style={{
              padding: "14px",
              background: "var(--snow)",
              borderRadius: 12,
              marginBottom: 16,
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: "var(--stone)",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              🌐 {t("language") || "Language"}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {LANGS.map(([code, , fullLabel]) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  style={{
                    flex: 1,
                    padding: "10px 6px",
                    border:
                      lang === code
                        ? "2px solid var(--emerald)"
                        : "2px solid transparent",
                    borderRadius: 10,
                    cursor: "pointer",
                    fontFamily: "var(--sans)",
                    fontSize: 13,
                    fontWeight: lang === code ? 700 : 500,
                    background:
                      lang === code ? "var(--emerald)" : "var(--foam)",
                    color: lang === code ? "#fff" : "var(--stone)",
                    transition: "all .2s",
                  }}
                >
                  {fullLabel}
                </button>
              ))}
            </div>
          </div>

          {/* Search in drawer */}
          <div
            style={{
              padding: "14px",
              background: "var(--snow)",
              borderRadius: 12,
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: "var(--stone)",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              🔍 {t("searchPlaceholder")}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={srch}
                onChange={(e) => setSrch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    go("products");
                  }
                }}
                placeholder={t("searchPlaceholder")}
                style={{
                  flex: 1,
                  border: "1.5px solid var(--mist)",
                  borderRadius: 9,
                  padding: "9px 14px",
                  fontFamily: "var(--sans)",
                  fontSize: 14,
                  color: "var(--ink)",
                  background: "#fff",
                  outline: "none",
                }}
              />
              <button
                onClick={() => go("products")}
                style={{
                  background: "var(--emerald)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 9,
                  padding: "0 14px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icons.Search size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* Drawer footer — Admin button */}
        {/* <div
          style={{
            padding: "14px 14px 24px",
            borderTop: "1px solid var(--foam)",
            flexShrink: 0,
          }}
        >
          <Button
            variant="gold"
            size="md"
            fullWidth
            onClick={() => go("admin")}
          >
            {t("admin")} Panel ↗
          </Button>
        </div> */}
      </div>

      {/* ── CSS: hide hamburger on desktop, show on mobile ── */}
      <style>{`
        @media (min-width: 769px) { .show-mobile { display: none !important; } }
        @media (max-width: 768px) { .hide-sm { display: none !important; } }
      `}</style>
    </>
  );
};

export default Navbar;
